import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@contexts/AuthContext';
import { LogIn, Eye, EyeOff, CheckCircle } from 'lucide-react';
import { supabase } from '@services/api.js';
import { isValidEmail } from '../utils/validation';
import { usePageTitle } from '@hooks/usePageTitle';
import ContourBG from '@components/ContourBG';

const Login = () => {
  usePageTitle('Sign in — Castle Atlas');
  const navigate = useNavigate();
  const { signIn } = useAuth();

  const [form, setForm]           = useState({ email: '', password: '' });
  const [showPw, setShowPw]       = useState(false);
  const [loading, setLoading]     = useState(false);
  const [error, setError]         = useState('');
  const [resetSent, setResetSent] = useState(false);
  const [showReset, setShowReset] = useState(false);
  const [resetEmail, setResetEmail]   = useState('');
  const [resetLoading, setResetLoading] = useState(false);

  /* ── Atlas Plates shell: theme detection (same pattern as Dashboard/Settings) ── */
  const [isDark, setIsDark] = useState(() =>
    typeof window !== 'undefined' && document.documentElement.classList.contains('dark')
  );
  useEffect(() => {
    const obs = new MutationObserver(() => setIsDark(document.documentElement.classList.contains('dark')));
    obs.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
    return () => obs.disconnect();
  }, []);

  const handleChange = (e) => {
    setForm(prev => ({ ...prev, [e.target.name]: e.target.value }));
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!form.email || !form.password) {
      setError('Please fill in all fields.');
      return;
    }

    // STRICT EMAIL VALIDATION
    if (!isValidEmail(form.email)) {
      setError('Please enter a valid email address.');
      return;
    }

    setLoading(true);
    try {
      const { data } = await signIn({ email: form.email, password: form.password });
      const userId = data?.user?.id;
      if (userId) {
        const { data: profileData } = await supabase
          .from('profiles')
          .select('onboarding_completed')
          .eq('id', userId)
          .single();

        if (profileData?.onboarding_completed) {
          navigate('/dashboard');
        } else {
          navigate('/onboarding');
        }
      }
    } catch (err) {
      setError(err.message || 'Failed to sign in. Please check your credentials.');
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (!isValidEmail(resetEmail)) {
      alert('Please enter a valid email address.');
      return;
    }
    setResetLoading(true);
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(resetEmail, {
        redirectTo: `${window.location.origin}/reset-password`,
      });
      if (error) throw error;
      setResetSent(true);
    } catch (err) {
      alert(err.message);
    } finally {
      setResetLoading(false);
    }
  };

  /* ── Atlas Plates tokens ── */
  const pageBg = isDark ? '#0e0d0c' : '#faf9f7';
  const dashColor = isDark ? '#57534e' : '#d4cdc7';
  const mutedColor = isDark ? '#78716c' : '#a8a29e';

  return (
    <div id="main-content" tabIndex={-1} style={{
      minHeight: '100vh', position: 'relative',
      backgroundColor: pageBg,
      transition: 'background-color 0.3s ease',
    }}>
      {/* Contour header — same treatment as Dashboard / Settings */}
      <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 180, overflow: 'hidden', pointerEvents: 'none' }}>
        <ContourBG isDark={isDark} size="compact" opacity={0.9} />
      </div>

      <div className="min-h-screen flex items-center justify-center p-4 font-sans" style={{ position: 'relative', zIndex: 1 }}>
        <div className="w-full max-w-md">

          {/* Plate eyebrow */}
          <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 14 }}>
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 10 }}>
              <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11, fontWeight: 600, color: '#ea580c', letterSpacing: '0.14em' }}>THE GATE</span>
              <span style={{ width: 22, height: 1, background: dashColor, display: 'inline-block' }} />
              <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11, fontWeight: 600, color: mutedColor, letterSpacing: '0.14em', textTransform: 'uppercase' }}>
                {showReset || resetSent ? 'Reset' : 'Sign in'}
              </span>
            </div>
          </div>

          {/* Logo Section */}
          <div className="flex flex-col items-center mb-8">
            <div className="mb-4">
              <img src="/logo.svg" alt="Castle Atlas" className="w-12 h-12 dark:brightness-0 dark:invert" />
            </div>
            <h1 className="text-2xl font-bold tracking-tight text-stone-900 dark:text-white" style={{ fontFamily: 'var(--font-display)', letterSpacing: '-0.02em' }}>
              {showReset || resetSent ? 'Reset your password' : 'Welcome back to Atlas'}
            </h1>
            <p className="text-sm text-stone-500 dark:text-stone-400 mt-1 text-center px-4">
              {showReset || resetSent
                ? "Enter your email and we'll send you a reset link."
                : 'Sign in to continue your streak and level up your skills.'}
            </p>
          </div>

          <div className="bg-white dark:bg-stone-900 rounded-3xl shadow-xl border border-stone-200 dark:border-stone-800 p-8 md:p-10 relative overflow-hidden">
            {error && !showReset && !resetSent && (
              <div className="mb-6 p-3 bg-red-50 dark:bg-red-900/20 border border-red-100 dark:border-red-800 rounded-xl text-red-600 dark:text-red-400 text-xs font-medium animate-shake" role="alert">
                {error}
              </div>
            )}

            {resetSent ? (
              <div className="text-center py-4">
                <div className="w-16 h-16 bg-green-50 dark:bg-green-900/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircle className="w-8 h-8 text-green-500" />
                </div>
                <h2 className="text-lg font-bold text-stone-900 dark:text-white mb-2">Check your email</h2>
                <p className="text-sm text-stone-500 dark:text-stone-400 mb-6">We've sent password reset instructions to {resetEmail}.</p>
                <button onClick={() => { setResetSent(false); setShowReset(false); setError(''); }} className="text-sm font-bold text-brand-600 hover:text-brand-700">Back to sign in</button>
              </div>
            ) : showReset ? (
              <form onSubmit={handleResetPassword} className="space-y-4">
                <div className="space-y-1.5">
                  <label htmlFor="reset-email" className="text-[10px] font-bold text-stone-400 uppercase tracking-widest ml-1">Email Address</label>
                  <input id="reset-email" type="email" autoComplete="email" value={resetEmail} onChange={(e) => setResetEmail(e.target.value)} placeholder="jessica@example.com" className="input" required />
                </div>
                <button type="submit" disabled={resetLoading} className="btn-primary w-full py-3">
                  {resetLoading ? 'Sending...' : 'Send Reset Link'}
                </button>
                <button type="button" onClick={() => { setShowReset(false); setError(''); }} className="w-full text-sm text-stone-500 hover:text-stone-700 dark:hover:text-stone-300 font-medium">
                  Back to sign in
                </button>
              </form>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-5">
                <div className="space-y-1.5">
                  <label htmlFor="login-email" className="text-[10px] font-bold text-stone-400 uppercase tracking-widest ml-1">Email Address</label>
                  <input id="login-email" type="email" name="email" autoComplete="email" value={form.email} onChange={handleChange} placeholder="jessica@example.com" className="input" />
                </div>

                <div className="space-y-1.5">
                  <div className="flex justify-between items-center px-1">
                    <label htmlFor="login-password" className="text-[10px] font-bold text-stone-400 uppercase tracking-widest">Password</label>
                    <button type="button" onClick={() => { setShowReset(true); setResetEmail(form.email); setError(''); }} className="text-[10px] text-stone-400 hover:text-stone-500 dark:hover:text-stone-300 font-medium uppercase tracking-widest">
                      Forgot password?
                    </button>
                  </div>
                  <div className="relative">
                    <input id="login-password" type={showPw ? 'text' : 'password'} name="password" autoComplete="current-password" value={form.password} onChange={handleChange} placeholder="••••••••" className="input pr-10" />
                    <button type="button" onClick={() => setShowPw(p => !p)} aria-label={showPw ? 'Hide password' : 'Show password'} className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-400 hover:text-stone-600">
                      {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>

                <button type="submit" disabled={loading} className="btn-primary w-full flex items-center justify-center gap-2 mt-2 py-3">
                  {loading ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <><LogIn className="w-4 h-4" />Sign in</>}
                </button>
              </form>
            )}
          </div>

          <p className="text-center text-sm text-stone-500 dark:text-stone-400 mt-8">
            Don't have an account?{' '}
            <Link to="/signup" className="text-brand-600 hover:text-brand-700 font-bold">Start for free</Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Login;
